import { Auth0Provider } from '@auth0/auth0-react';
import { FC } from 'react';
import { Auth0ProviderWithHistoryProps } from '../types';
import { useAuthRedirect } from '../hooks';
import { getAuth0Config } from '../constants';

/**
 * Auth0 Provider component with history/redirection support.
 *
 * This component wraps the Auth0Provider from @auth0/auth0-react and adds
 * custom redirect logic. For single-tenant applications, the organization
 * is passed via the VITE_ORGANIZATION_ID environment variable and included
 * in the authorization parameters.
 *
 * @param props - Component props including router and optional organization
 * @returns Auth0Provider with custom redirect callback
 *
 * @example
 * ```tsx
 * const router = createBrowserRouter(routes);
 *
 * root.render(
 *   <Auth0ProviderWithHistory router={router} organization={orgId}>
 *     <RouterProvider router={router} />
 *   </Auth0ProviderWithHistory>
 * );
 * ```
 */
export const Auth0ProviderWithHistory: FC<Auth0ProviderWithHistoryProps> = ({
    children,
    router,
    organization,
    config: configOverrides,
}): React.ReactElement => {
    // Get the complete Auth0 configuration
    const config = getAuth0Config(configOverrides);

    // Use custom hook to handle redirect logic
    const { onRedirectCallback } = useAuthRedirect({ router });

    // Build Auth0Provider props
    // Organization is passed both in authorizationParams (for the URL) and as a prop
    // to ensure it's included in all Auth0 operations
    const auth0ProviderProps: import('@auth0/auth0-react').Auth0ProviderOptions =
        {
            domain: config.domain,
            clientId: config.clientId,
            authorizationParams: {
                redirect_uri: config.authorizationParams.redirect_uri,
                audience: config.authorizationParams.audience,
                scope: config.authorizationParams.scope,
                // Include organization in authorizationParams to ensure it's in the auth URL
                ...(organization && { organization }),
            },
            useRefreshTokens: config.useRefreshTokens,
            useRefreshTokensFallback: config.useRefreshTokensFallback,
            cacheLocation: config.cacheLocation,
            onRedirectCallback,
            // Also pass organization as a prop for Auth0 internal use
            ...(organization && { organization }),
        };

    return <Auth0Provider {...auth0ProviderProps}>{children}</Auth0Provider>;
};

export default Auth0ProviderWithHistory;
